import { Controller } from '@hotwired/stimulus'
import debounce from 'lodash/debounce'
import { watch } from 'vue'

import type { ColumnConfig } from '@app/application/report/column-editor/types'
import type { HeapProperties } from '@app/adapters/heap'

import { createApp } from '@app/adapters/create-app'
import { SfLog } from '@app/adapters/logger'
import {
  applyErrorFrom,
  fetchAppliedColumnsRequest,
  fetchColumnsPreviewRequest,
} from '@app/application/report/api/columns'
import { columnsDiff } from '@app/application/report/column-editor/column-diff'
import ColumnEditorDrawer from '@app/application/report/column-editor/components/column-editor-drawer.vue'
import { trackInlineColumns } from '@app/application/report/column-editor/track'
import {
  COLUMN_EDITOR_APPLIED,
  COLUMN_EDITOR_CLOSED,
  COLUMN_EDITOR_OPENED,
  FORECAST_CHANGED,
} from '@app/application/report/column-editor/events'
import type { ForecastChangedDetail } from '@app/application/report/column-editor/events'
import {
  blankReportColumns,
  findReportTable,
  isPreviewing,
  renderPreviewColumns,
  restoreReportColumns,
} from '@app/application/report/column-editor/report-column-preview'
import { swapReportTable } from '@app/application/report/column-editor/report-table-swap'
import { reportTrackingProperties } from '@app/application/report/report-tracking'
import { useColumnEditor } from '@app/application/report/column-editor/use-column-editor'
import getPiniaInstance from '@app/shared/pinia/instance'

// Long enough that dragging a row or clicking through a dropdown settles into a
// single request, short enough that one edit still reads as immediate.
const PREVIEW_DEBOUNCE_MS = 250

// The frame the report table is rendered into (reports/show.html.slim). It loads
// lazily, so the table can arrive after the drawer is already open.
const TABLE_FRAME_ID = 'report-table'

export default class extends Controller {
  static targets = ['drawer', 'openButton']

  private drawerTarget!: HTMLElement

  // The subnav button. Rendered disabled, because until the report table is on
  // the page there is nothing for an apply to replace — see syncOpenButton.
  private openButtonTarget!: HTMLButtonElement

  private hasOpenButtonTarget!: boolean

  private app: ReturnType<typeof createApp> | null = null

  private store: ReturnType<typeof useColumnEditor> | null = null

  private stopWatchers: (() => void)[] = []

  // Only the newest preview counts: edits outpace the responses, and an earlier
  // one landing last would leave the table showing a config the drawer has
  // already moved on from.
  private previewRequest = 0

  // Bumped on every open, so a response can tell the drawer it was asked from
  // apart from a later one. Closing alone is not enough to tell them apart: an
  // apply the user backed out of would find the drawer open again and put its
  // columns — the ones from before the cancel — on the report, closing the
  // drawer over the edits made since.
  private openGeneration = 0

  private openedAt = 0

  // Read while the page is still on screen; Abandoned fires after the body swap.
  private reportProperties: HeapProperties = {}

  // Only a drawer session that ends without an apply reports itself abandoned.
  private appliedThisSession = false

  // Counted over the page, not the drawer session: a second apply means the
  // first was not what they wanted, whether or not the drawer closed between.
  private applyIndex = 0

  // Stimulus scans once the document is parsed, so a table rendered inline is
  // already there to be found. One that the frame brings in is not, and
  // `reapplyPreview` answers for it.
  openButtonTargetConnected(): void {
    this.syncOpenButton()
  }

  // Applying replaces the report table, so there has to be one to replace: with
  // the frame still loading, Apply fails on a precondition it can only report as
  // a generic failure. Off until the table is there.
  //
  // Except while the drawer is open, where this button is what closes it — a
  // frame reload taking the table away mid-session must not strand the user with
  // the ✕ as their only way out. Which is also the one window this doesn't cover:
  // between a reload starting and its table landing the button stays as it was,
  // because nothing tells us the table went. Apply's own guard still refuses.
  private syncOpenButton(): void {
    if (!this.hasOpenButtonTarget) return

    this.openButtonTarget.disabled = !this.store?.isOpen && !findReportTable()
  }

  connect(): void {
    document.addEventListener('turbo:frame-load', this.reapplyPreview)
    document.addEventListener(FORECAST_CHANGED, this.handleForecastChanged)

    // Turbolinks caches the page on the way out, and `disconnect` is too late to
    // undo the preview for it: Stimulus only disconnects once the incoming body
    // has replaced the outgoing one, by which point the table the preview is
    // painted over is detached and `findReportTable` looks straight past it. The
    // snapshot would then be cached mid-preview, and going back to it would show
    // the report with its own columns hidden behind preview cells for nothing.
    // This fires while the outgoing page is still the document.
    document.addEventListener('turbolinks:before-cache', this.teardownPreview)
  }

  // Triggered by the subnav button, which carries the report id and the period
  // set currently on screen as action params
  // (`data-reports--column-editor-report-id-param` and
  // `-report-period-set-id-param`).
  toggle(
    event: Event & {
      params: { reportId: number; reportPeriodSetId?: number }
    }
  ): void {
    const store = this.mountDrawer()

    // The drawer has no backdrop, so the button stays in front of it and is the
    // second thing a user reaches for to put it away. Closing through the store
    // is all it takes: the `isOpen` watcher reports the session and takes the
    // preview off, exactly as it does for the drawer's own ✕.
    if (store.isOpen) {
      store.close()
      return
    }

    this.openGeneration += 1

    // A report is always viewed through some period set, but only an explicitly
    // selected one is rendered as a param.
    store.configure({
      reportId: event.params.reportId,
      reportPeriodSetId: event.params.reportPeriodSetId ?? null,
    })
    store.open()

    this.openedAt = Date.now()
    this.appliedThisSession = false
    this.reportProperties = reportTrackingProperties()

    // The report keeps its own columns until there is a preview to put in their
    // place — hiding them up front empties the table for as long as the first
    // request takes, which reads as the report having been wiped. They do go
    // quiet straight away, so the preview arriving only moves columns about
    // instead of also blanking every figure at that moment.
    blankReportColumns(findReportTable())

    // The forecast column is another controller's, and the drawer lists it: this
    // is what it answers with what the report has (see events.ts). Sent after
    // the store is open so the answer lands on a drawer that is on screen.
    document.dispatchEvent(new CustomEvent(COLUMN_EDITOR_OPENED))

    // After the forecast has answered, or the event would say every report is
    // without one.
    trackInlineColumns('Opened', store.drawerContext())

    // Scheduled rather than fired: loading the columns changes them, which the
    // watcher below also schedules a preview for, and one request is enough.
    // Re-opening a drawer whose columns are already loaded changes nothing, so
    // this is the only thing asking for a preview on that path.
    void store.load().then(this.schedulePreview)
  }

  disconnect(): void {
    document.removeEventListener('turbo:frame-load', this.reapplyPreview)
    document.removeEventListener(FORECAST_CHANGED, this.handleForecastChanged)
    document.removeEventListener(
      'turbolinks:before-cache',
      this.teardownPreview
    )

    this.stopWatchers.forEach((stop) => stop())
    this.stopWatchers = []

    // Leaving the page with the drawer open is abandoning it too. Safe here
    // because the watcher that would also report it has just been stopped.
    if (this.store?.isOpen) this.trackAbandoned()

    this.teardownPreview()

    this.app?.unmount()
    this.app = null
    this.store = null
  }

  private mountDrawer(): ReturnType<typeof useColumnEditor> {
    if (this.store) return this.store

    this.app = createApp(ColumnEditorDrawer)
    this.app.mount(this.drawerTarget)

    // Resolved here, not in a field initializer: the Pinia instance is a page
    // singleton that gets reset on navigation, so the store must come from the
    // same instance the drawer app was just mounted with.
    const store = useColumnEditor(getPiniaInstance())
    this.store = store

    this.stopWatchers.push(
      // Any change to the list — a value edited, a column added or removed, the
      // order dragged about — is a different set of columns to preview.
      watch(() => store.columns, this.schedulePreview, { deep: true }),
      watch(
        () => store.isOpen,
        (isOpen) => {
          this.syncOpenButton()

          if (isOpen) return

          this.trackAbandoned()
          this.teardownPreview()
          document.dispatchEvent(new CustomEvent(COLUMN_EDITOR_CLOSED))
        }
      ),
      // The drawer asks; replacing the report table is this controller's job.
      watch(
        () => store.applyRequests,
        () => void this.applyColumns()
      )
    )

    return store
  }

  private schedulePreview = debounce(
    () => void this.refreshPreview(),
    PREVIEW_DEBOUNCE_MS
  )

  private async refreshPreview(): Promise<void> {
    const store = this.store
    if (!store?.isOpen || !store.reportId) return

    const table = findReportTable()
    if (!table) return

    // A load that failed is a preview that is never coming, and the report has
    // been sitting there saying nothing since the drawer opened (see `open`). So
    // it gets its own columns back and reads as itself again while the drawer
    // offers a retry — which lands here again, with something to show this time.
    if (store.status === 'error') {
      restoreReportColumns(table)
      return
    }

    // Nothing to preview until the drawer knows what the report's columns are.
    // `configure` empties them on the way to loading them, and the watcher below
    // schedules a preview for that — which, if the load is slower than the
    // debounce, would ask what an empty set of columns looks like and answer by
    // collapsing the report to nothing but its row labels. The report stays quiet
    // for that: the columns are on their way.
    if (store.status !== 'loaded') return

    // Nothing previewed means the report's own figures are still on screen, at
    // their own widths — a retried load lands here having just had them given
    // back. They go quiet for the same reason they do on open: so the preview
    // arriving only moves columns about instead of also resizing them.
    if (!isPreviewing(table)) blankReportColumns(table)

    const request = (this.previewRequest += 1)

    try {
      const preview = await fetchColumnsPreviewRequest({
        reportId: store.reportId,
        columns: this.columnConfigs(),
      })

      if (this.previewIsStale(request, table)) return

      renderPreviewColumns(table, preview)
      store.previewSucceeded()
    } catch (error) {
      // Whatever was previewed stays on screen: it is one edit out of date,
      // which beats dropping back to real columns the drawer isn't editing. With
      // nothing previewed yet there is nothing to keep, and the report has been
      // sitting there saying nothing — so it gets its own columns back.
      //
      // Superseded requests keep their hands off the table, and say nothing about
      // what the drawer is showing either: a newer preview is on its way, will
      // report for itself, and giving the real columns back for it to hide again
      // is the reflow the report was blanked to avoid.
      if (!this.previewIsStale(request, table)) {
        if (!isPreviewing(table)) restoreReportColumns(table)

        // Otherwise the drawer's controls read as doing nothing at all — or
        // worse, the table keeps a preview that no longer matches the list.
        store.previewErrored()
      }

      SfLog.error('Failed to preview the report columns', {
        error,
        extra: { reportId: store.reportId },
      })
    }
  }

  // A preview is worth putting on screen only if it is still the newest one, the
  // drawer is still open, and the table it was captured against is still the
  // table on the page.
  //
  // That last one is not covered by the counter. `refreshPreview` gives up
  // before bumping it when there is no table to preview into — which is what a
  // frame reload landing on the spinner or the error block leaves behind — so
  // the request in flight against the table that just went away is never
  // superseded, and would otherwise render into a detached node: no preview on
  // screen, a table nobody can reach marked as carrying one, and tooltips hung
  // off the same unreachable DOM.
  private previewIsStale(request: number, table: HTMLTableElement): boolean {
    return (
      request !== this.previewRequest ||
      !this.store?.isOpen ||
      !table.isConnected
    )
  }

  // Whether the drawer this apply was asked from is still the one on screen —
  // closed, navigated away from, or closed and opened again all mean its answer
  // is no longer wanted.
  private applyIsStale(generation: number): boolean {
    return !this.store?.isOpen || this.openGeneration !== generation
  }

  // What the drawer holds, as the server wants it. Shared by the preview and the
  // apply so the two cannot drift into asking about different columns.
  private columnConfigs(): ColumnConfig[] {
    return (this.store?.columns ?? []).map((column) => ({
      type: column.type,
      values: column.values,
    }))
  }

  // Renders the report for the columns the drawer holds and puts that table on
  // the page, then closes the drawer: what was being previewed is now what the
  // report shows. Nothing is saved — the columns are applied for as long as this
  // table is on screen, and the next load brings the report's own back.
  private async applyColumns(): Promise<void> {
    const store = this.store
    if (!store?.isOpen || !store.reportId) return
    if (store.status !== 'loaded' || store.applying) return

    store.applyStarted()

    const generation = this.openGeneration

    // Captured before the request: the drawer stays editable while it is in
    // flight, so reading the list back after would describe other columns.
    const diff = columnsDiff(store.loadedColumns, store.columns)

    this.applyIndex += 1

    // Held rather than read back after the await, so the events of this apply
    // cannot pick up a later one's number.
    const applyIndex = this.applyIndex

    // Both counters are the page's, not the drawer session's: an apply on the
    // fourth trip through the drawer is a different story from one on the first,
    // and so is a fourth apply.
    //
    // Read before the request, like the diff: Clear stays clickable meanwhile.
    const applyContext = {
      apply_index: applyIndex,
      ...store.drawerContext(),
    }

    const applyProperties = { ...applyContext, ...diff.properties }

    // The frame can still be loading its table, and its own load would replace
    // the applied one moments later, with the drawer already closed by then.
    // Refused after the properties are built: this is the one apply failure the
    // server never hears about, so nothing else would count it.
    if (!findReportTable()) {
      store.applyErrored()
      store.applyFinished()

      trackInlineColumns('Apply Failed', {
        apply_reason: 'no_report_table',
        ...applyProperties,
      })

      return
    }

    try {
      const html = await fetchAppliedColumnsRequest({
        reportId: store.reportId,
        columns: this.columnConfigs(),
      })

      // The drawer can be closed — or the page navigated away from — while the
      // render is on its way, and both have already given the report its own
      // columns back. Putting this table on the page now would change the
      // report to columns the user backed out of, with nothing on screen left
      // to say where they came from. A drawer that has since been opened again
      // is the same cancelled request, only harder to see: it is open, but not
      // the one that asked for this.
      if (this.applyIsStale(generation)) return

      // Swapped before the drawer closes, so a table that can't be put on the
      // page leaves the drawer open to say so. Closing then tears the preview
      // down against the new table, which disposes the old table's tooltips
      // without finding anything of its own to undo.
      swapReportTable(html)

      // The table this replaced is the one a forecast column was built on, so
      // that column has to be put back on the new one. Sent while the table is
      // already on the page: the forecast controller acts on the table its own
      // Stimulus target reports, which is the one that just landed.
      document.dispatchEvent(new CustomEvent(COLUMN_EDITOR_APPLIED))

      // The columns the report is showing from here on, so the drawer can tell a
      // list worth applying from the one already on the page.
      store.applyRendered()

      this.appliedThisSession = true
      trackInlineColumns('Applied', applyProperties)

      // One event per column, so a single dropdown can be counted on its own.
      // With the apply's own counters, so a column reads back to its attempt.
      diff.changes.forEach((change) => {
        trackInlineColumns('Column Applied', { ...applyContext, ...change })
      })

      store.close()
    } catch (error) {
      // Only while the drawer that asked for it is still there to show it.
      // Closing clears both errors precisely so the next open doesn't greet the
      // user with a complaint about a request from before it — and by then the
      // report has its own columns back, so there is nothing on screen the
      // complaint is about. Same guard the success path takes.
      if (!this.applyIsStale(generation)) {
        const reason = applyErrorFrom(error)
        store.applyErrored(reason)

        // The only count of a refused apply: the 422 raises nothing server-side
        // and SfLog drops 4xx before Sentry.
        trackInlineColumns('Apply Failed', {
          apply_reason: reason,
          ...applyProperties,
        })
      }

      SfLog.error('Failed to apply the report columns', {
        error,
        extra: { reportId: store.reportId },
      })
    } finally {
      // Only this apply's own session may un-spin Apply; close already cleared it.
      if (!this.applyIsStale(generation)) store.applyFinished()
    }
  }

  // The report the session opened on: disconnect runs after the incoming body
  // has replaced the outgoing one, so the document then names the destination.
  private trackAbandoned(): void {
    const store = this.store
    if (!store || this.appliedThisSession) return

    trackInlineColumns(
      'Abandoned',
      {
        ...store.drawerActivity(),
        drawer_seconds_open: Math.round((Date.now() - this.openedAt) / 1000),
      },
      this.reportProperties
    )
  }

  // The table frame loads lazily and reloads on its own (a period set change, a
  // finished async render), each time replacing the table the preview was
  // applied to. The fresh table shows its own columns until the preview lands on
  // it, same as on open.
  private reapplyPreview = (event: Event): void => {
    if ((event.target as HTMLElement).id !== TABLE_FRAME_ID) return

    // Before the drawer check below: the frame having brought a table in is what
    // makes the button usable, whether or not the drawer is open over it.
    this.syncOpenButton()

    if (!this.store?.isOpen) return

    blankReportColumns(findReportTable())

    // The fresh table needs a preview now, so a debounced one queued behind an
    // edit made just before the reload is a second request for the same state.
    this.schedulePreview.cancel()

    void this.refreshPreview()
  }

  // What the forecast column answers with when the drawer opens, and again
  // whenever it changes — clearing the forecast while the drawer is open is the
  // one change it can go through under it.
  private handleForecastChanged = (event: Event): void => {
    const { year } = (event as CustomEvent<ForecastChangedDetail>).detail

    this.store?.setForecastYear(year)
  }

  // Bound, because it is a document listener as well as a method.
  private teardownPreview = (): void => {
    this.schedulePreview.cancel()
    restoreReportColumns(findReportTable())
  }
}
